// src/hooks/useWidgets.js - Simplified Hook for Pattern B
import { useMemo } from 'react';
import useWidgetStore from '../stores/WidgetStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing widget data from WidgetStore
 * 
 * SIMPLIFIED: Aligned with Pattern B backend architecture
 * - Removed all draft-related complexity
 * - Eliminated currentEditingWidget derivation that caused remounting
 * - Direct access to selectedWidget for stable object references
 * - Clean interface matching simplified backend operations
 * 
 * @returns {object} Widget data and operations for Pattern B
 */
export const useWidgets = () => {
  const socket = useSocket();
  
  // ================================
  // SIMPLIFIED STATE ACCESS
  // ================================
  
  // Main widget data - direct store access
  const widgets = useWidgetStore(state => state.widgets);
  const widgetsLoading = useWidgetStore(state => state.widgetsLoading);
  const widgetsError = useWidgetStore(state => state.widgetsError);
  
  // Selected widget - DIRECT ACCESS (no more complex derivation)
  const selectedWidget = useWidgetStore(state => state.selectedWidget);
  const selectedWidgetLoading = useWidgetStore(state => state.selectedWidgetLoading);
  const selectedWidgetError = useWidgetStore(state => state.selectedWidgetError);
  
  // Operation states
  const creatingWidget = useWidgetStore(state => state.creatingWidget);
  const savingWidget = useWidgetStore(state => state.savingWidget);
  const publishingWidget = useWidgetStore(state => state.publishingWidget);
  const deletingWidget = useWidgetStore(state => state.deletingWidget);
  const generatingEmbed = useWidgetStore(state => state.generatingEmbed);
  
  // Embed codes
  const embedCodes = useWidgetStore(state => state.embedCodes);
  
  // Filter state
  const selectedType = useWidgetStore(state => state.selectedType);
  const setSelectedType = useWidgetStore(state => state.setSelectedType);
  
  // ================================
  // WIDGET OPERATIONS
  // ================================
  
  const loadWidgets = useWidgetStore(state => state.loadWidgets);
  const loadWidgetDetails = useWidgetStore(state => state.loadWidgetDetails);
  const createWidget = useWidgetStore(state => state.createWidget);
  const updateWidget = useWidgetStore(state => state.updateWidget);
  const publishWidget = useWidgetStore(state => state.publishWidget);
  const toggleWidgetStatus = useWidgetStore(state => state.toggleWidgetStatus);
  const deleteWidget = useWidgetStore(state => state.deleteWidget);
  const generateEmbedCode = useWidgetStore(state => state.generateEmbedCode);
  
  // ================================
  // UTILITY FUNCTIONS
  // ================================
  
  const clearWidgetsError = useWidgetStore(state => state.clearWidgetsError);
  const clearSelectedWidgetError = useWidgetStore(state => state.clearSelectedWidgetError);
  const clearSelectedWidget = useWidgetStore(state => state.clearSelectedWidget);
  const refreshWidgets = useWidgetStore(state => state.refreshWidgets);
  const getWidgetById = useWidgetStore(state => state.getWidgetById);
  const getWidgetsByType = useWidgetStore(state => state.getWidgetsByType);
  const getEmbedCode = useWidgetStore(state => state.getEmbedCode);
  
  // Computed value getters from store
  const getFilteredWidgets = useWidgetStore(state => state.getFilteredWidgets);
  const getWidgetCounts = useWidgetStore(state => state.getWidgetCounts);
  const getHasWidgets = useWidgetStore(state => state.getHasWidgets);
  const getHasActiveWidgets = useWidgetStore(state => state.getHasActiveWidgets);
  
  // Connection state
  const isConnected = useWidgetStore(state => state.isConnected);
  const _initialized = useWidgetStore(state => state._initialized);
  
  // ================================
  // FALLBACK INITIALIZATION
  // ================================
  
  // Fallback initialization check (safety net)
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('useWidgets: Store not initialized at app level, trying fallback');
    socket.initializeStore('WidgetStore');
  }
  
  // ================================
  // COMPUTED VALUES (Simple memoization)
  // ================================
  
  const filteredWidgets = useMemo(() => getFilteredWidgets(), [widgets, selectedType]);
  const widgetCounts = useMemo(() => getWidgetCounts(), [widgets]);
  const hasWidgets = useMemo(() => getHasWidgets(), [widgets]);
  const hasActiveWidgets = useMemo(() => getHasActiveWidgets(), [widgets]);
  
  // ================================
  // SIMPLIFIED INTERFACE
  // ================================
  
  return {
    // ===== MAIN WIDGET DATA =====
    widgets,
    widgetsLoading,
    widgetsError,
    filteredWidgets,
    
    // ===== SELECTED WIDGET DATA =====
    // IMPORTANT: Use selectedWidget directly - no more currentEditingWidget
    selectedWidget,
    selectedWidgetLoading,
    selectedWidgetError,
    
    // ===== OPERATION STATES =====
    creatingWidget,
    savingWidget,
    publishingWidget,
    deletingWidget,
    generatingEmbed,
    
    // ===== EMBED CODES =====
    embedCodes,
    
    // ===== FILTER STATE =====
    selectedType,
    setSelectedType,
    
    // ===== WIDGET OPERATIONS =====
    loadWidgets,
    loadWidgetDetails,
    createWidget,           // Creates inactive widget immediately
    updateWidget,           // Updates draft configuration
    publishWidget,          // Publishes draft → published config
    toggleWidgetStatus,     // Activates/deactivates widget
    deleteWidget,
    generateEmbedCode,
    
    // ===== UTILITY FUNCTIONS =====
    clearWidgetsError,
    clearSelectedWidgetError,
    clearSelectedWidget,
    refreshWidgets,
    getWidgetById,
    getWidgetsByType,
    getEmbedCode,
    
    // ===== COMPUTED VALUES =====
    widgetCounts,
    hasWidgets,
    hasActiveWidgets,
    
    // ===== CONNECTION STATE =====
    isConnected: socket.isAuthenticated,
    
    // ===== LEGACY ALIASES (for backward compatibility) =====
    loading: widgetsLoading,
    selectedLoading: selectedWidgetLoading,
    error: widgetsError,
    selectedError: selectedWidgetError,
    clearError: clearWidgetsError,
    clearSelectedError: clearSelectedWidgetError
  };
};

export default useWidgets;